import { Box, Button, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CompanyType } from "../../Enums/CompanyType";
import { getCompanyPortalName } from "../../Utils/environment";
import { RequestSourceTypeEnum } from "../Enums/RequestSourceTypeEnum";
import { RequestFormContext } from "../RequestFormContext";
import { CheckmarkIcon, DownloadFileIcon } from "vig-ui-library";

export type RequestSubmitSuccessViewProps = Omit<RequestFormContext, "pathToProp"> & {
    companyType: CompanyType;
    onOpenRequestDetail?: () => void;
    onClose?: () => void;
};

export const RequestSubmitSuccessView = ({
    request,
    companyType,
    downloadGeneratedSubmittedRequestDocument,
    onOpenRequestDetail,
    onClose,
}: RequestSubmitSuccessViewProps) => {
    const theme = useTheme();
    const successColor = theme.palette.success.main;
    const isClientRequest = request.current.sourceTypeId === RequestSourceTypeEnum.KLIENT;
    const requestId = request.current.requestId;
    const companyName = getCompanyPortalName(companyType);

    const headline = isClientRequest
        ? "Vaša žiadosť bola úspešne odoslaná poisťovni"
        : "Žiadosť bola úspešne podaná do poisťovne";

    const description = isClientRequest
        ? `O jej schválení vás budeme informovať prostredníctvom emailu, sms správy alebo notifikáciou v portáli ${companyName}. Potvrdenie o vytvorení žiadosti sme odoslali na vašu emailovú adresu.`
        : `Žiadosť bola klientom potvrdená a odoslaná poisťovni. O jej ďalšom vybavení vás budeme informovať prostredníctvom emailu, sms správy alebo upozornením v portáli ${companyName}.`;
    const hasGeneratedSubmittedRequestDocument = !!request.current.files?.some(
        (file) => file?.taxonomy?.isRequestDocument === true
    );

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", p: 2 }}>
            <Box
                sx={{
                    backgroundColor: theme.palette.common.white,
                    width: "100%",
                    maxWidth: "682px",
                    minHeight: "528px",
                    borderRadius: "7px",
                    p: theme.spacing(6),
                    boxShadow: `20px 20px 50px 0px ${alpha(theme.palette.grey[900], 0.04)}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: theme.spacing(4),
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: theme.spacing(3) }}>
                    <CheckmarkIcon width={64} height={64} color={successColor} />

                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: theme.spacing(1.5) }}>
                        <Typography
                            variant="h1"
                            component="p"
                            textAlign="center"
                            sx={{
                                fontSize: theme.typography.pxToRem(32),
                                fontWeight: theme.typography.fontWeightBold,
                                lineHeight: 1,
                                letterSpacing: 0,
                                color: successColor,
                            }}
                        >
                            {headline}
                        </Typography>

                        <Typography
                            variant="body1"
                            textAlign="center"
                            sx={{
                                maxWidth: "560px",
                                color: theme.palette.grey[800],
                            }}
                        >
                            {description}
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        minHeight: theme.spacing(9),
                        borderRadius: "7px",
                        p: theme.spacing(2),
                        gap: theme.spacing(2),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "nowrap",
                        backgroundColor: theme.palette.grey[100],
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.common.black,
                            minWidth: 0,
                            textAlign: "right",
                        }}
                    >
                        Číslo žiadosti:{" "}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: theme.typography.fontWeightBold,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                            }}
                        >
                            {requestId || "-"}
                        </Box>
                    </Typography>

                    {hasGeneratedSubmittedRequestDocument && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<DownloadFileIcon width={16} height={16} color={theme.palette.common.black} />}
                            onClick={downloadGeneratedSubmittedRequestDocument}
                            sx={{
                                typography: "body2",
                                fontWeight: theme.typography.fontWeightRegular,
                                fontStyle: "normal",
                                letterSpacing: 0,
                                leadingTrim: "none",
                                width: "fit-content",
                                height: 40,
                                borderRadius: "7px",
                                borderWidth: 1,
                                py: theme.spacing(1.125),
                                px: theme.spacing(1.5),
                                gap: 1,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.common.black,
                                borderColor: theme.palette.grey[200],
                                "&:hover": {
                                    backgroundColor: theme.palette.common.white,
                                    borderColor: theme.palette.grey[200],
                                },
                            }}
                        >
                            Stiahnuť potvrdenie
                        </Button>
                    )}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        width: "100%",
                        gap: theme.spacing(1.25),
                        justifyContent: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => onOpenRequestDetail?.()}
                        sx={{
                            typography: "button",
                            fontWeight: theme.typography.fontWeightMedium,
                            width: "fit-content",
                            minWidth: "138px",
                            height: 48,
                            borderRadius: "7px",
                            borderWidth: 1,
                            py: theme.spacing(1.5),
                            px: theme.spacing(2),
                            gap: 1,
                            letterSpacing: 0,
                            whiteSpace: "nowrap",
                        }}
                    >
                        Detail žiadosti
                    </Button>
                    {onClose && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onClose}
                            sx={{
                                typography: "button",
                                fontWeight: theme.typography.fontWeightMedium,
                                width: "fit-content",
                                minWidth: "91px",
                                height: 48,
                                borderRadius: "7px",
                                py: theme.spacing(1.5),
                                px: theme.spacing(2),
                                gap: 1,
                                letterSpacing: 0,
                                whiteSpace: "nowrap",
                            }}
                        >
                            Zatvoriť
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
